
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import axios from "axios";
import { ProductVO } from "@/views/biz/product/vo/product.vo";
import { CategoryVO } from "@/views/biz/menu/vo/category.vo";
import { BrandVO } from "@/views/biz/menu/vo/brand.vo";
import CommonUtils from "@/common-utils";
import Criteo from "@/criteo";
import { CriteoVO } from "@/vo/criteo.vo";
import values from "lodash-es/values";
import { Values } from "vue-i18n";
import VueSlider from "vue-slider-component";
import AdLogs from "@/ad-logs";

type DeliveryType = { productType: string; productTypeNm: string; active: boolean };
type Price = { start: number; end: number };

@Component
export default class Products extends Vue {
  // data
  ajaxInited = false;
  busy = false;
  active = true;

  deliveryTypes = [] as Array<DeliveryType>;
  category: CategoryVO = {} as CategoryVO;
  products: Array<ProductVO> = [];
  product: ProductVO = {} as ProductVO;
  categories: Array<CategoryVO> = [];
  brands: Array<BrandVO> = [];

  orderBy = ""; // 관련순/판매순/추천순/가격순/높은할인순

  showFilters = false;
  priceIncrease = {
    value: [] as any,
    min: 0,
    max: 0,
  };

  @Prop() cateCd!: string;
  @Prop() brandCd!: string;
  @Prop() couponCd!: string;
  @Prop() promotionCd!: string;
  @Prop() keyword!: string;

  // 조회조건
  @Watch("orderBy")
  onOrderChanged(): void {
    this.search(false);
  }
  // computed

  get filterPrices(): boolean {
    return this.priceIncrease.max == undefined && this.priceIncrease.min == this.priceIncrease.max ? false : true;
  }
  //최저금액 변경시
  isValidationStartPrice(): void {
    //최저금액이 맥스금액을 넘어갔을때 최저금액과 최대금액을 맥스로 잡아줌
    if (this.priceIncrease.value[0] > this.priceIncrease.max) {
      (this.$refs.slider as VueSlider).setValue([this.priceIncrease.max, this.priceIncrease.max]);
      return;
    }
    //최저금액이 최대 금액보다 컸을때
    if (this.priceIncrease.value[0] > this.priceIncrease.value[1]) {
      (this.$refs.slider as VueSlider).setValue([this.priceIncrease.value[0], this.priceIncrease.value[0]]);
      return;
    }
    //최저금액이 민금액보다 적을때
    if (this.priceIncrease.value[0] < this.priceIncrease.min) {
      (this.$refs.slider as VueSlider).setValue([this.priceIncrease.min, this.priceIncrease.value[1]]);
      return;
    }
  }
  //최대금액 변경시
  isValidationEndPrice(): void {
    //최고금액이 맥스금액을 넘어갔을때
    if (this.priceIncrease.value[1] > this.priceIncrease.max) {
      (this.$refs.slider as VueSlider).setValue([this.priceIncrease.value[0], this.priceIncrease.max]);
      return;
    }
    //최고금액이 민금액보다 작을때
    if (this.priceIncrease.value[1] < this.priceIncrease.min) {
      (this.$refs.slider as VueSlider).setValue([this.priceIncrease.min, this.priceIncrease.min]);
      return;
    }
    //최고금액이 최저금액보다 작을때
    if (this.priceIncrease.value[1] < this.priceIncrease.value[0]) {
      (this.$refs.slider as VueSlider).setValue([this.priceIncrease.value[1], this.priceIncrease.value[1]]);
      return;
    }
  }
  // watch
  @Watch("keyword") onKeywordChanged() {
    this.search(false);
  }

  //////////////
  // methods
  //////////////

  // 상품 목록
  search(loadMore?: boolean) {
    if (this.busy) {
      return;
    }
    this.busy = true;
    this.ajaxInited = false;

    if (!loadMore) {
      this.products = [];
    }

    const params = {
      cateCd: this.cateCd,
      brandCd: this.brandCd,
      couponCd: this.couponCd,
      promotionCd: this.promotionCd,
      keyword: this.keyword,
      orderBy: this.orderBy,
      orderAsc: this.orderBy.includes("Asc"),
      productTypes: this.deliveryTypes.filter((p) => {
        return p.active;
      }),
      categories: this.categories.filter((p) => {
        return p.active;
      }),
      brands: this.brands.filter((p) => {
        return p.active;
      }),
      start: this.priceIncrease.value[0],
      end: this.priceIncrease.value[1],
      pageStart: this.products.length,
    };

    axios.post("/v2/products", params).then((r) => {
      this.products = this.products.concat(r.data as Array<ProductVO>);
      this.showFilters = false;
      this.ajaxInited = true;
      if (r.data.length) {
        this.busy = false;
      }

      // 페이스북 픽셀 상품목록(검색) 추가 태그
      AdLogs.sendMetaPixelEventLogs('Search', new Map());

      // 크리테오 상품목록 정보 전송
      Criteo.putQuery({
        path: "",
        searchKeyword: params.keyword == undefined ? "" : params.keyword,
        categoryId: params.cateCd == undefined ? "" : params.cateCd,
        oneTag: Criteo.criteoTags.CATEGORY_SEARCH_LISTING_TAG,
        data: r.data,
      } as CriteoVO);
    });
  }

  // 검색 필터 카테고리 목록
  setCategories(): void {
    if (!this.brandCd) {
      return;
    }
    axios.get(`/brand/${this.brandCd}/categories`).then((r) => {
      const results = [] as Array<CategoryVO>;
      // 각 행에 active = false 세팅. FIXME 더 나은 방법이 있을거 같은데..
      for (const i in r.data) {
        const result: CategoryVO = r.data[i];
        result.active = false;
        results.push(result);
      }
      this.categories = results;
    });
  }

  // 검색 필터 브랜드 목록
  setBrands(): void {
    if (!this.cateCd) {
      return;
    }
    axios.get(`/category/${this.cateCd}/brands`).then((r) => {
      const results = [] as Array<BrandVO>;
      // 각 행에 active = false 세팅. FIXME 더 나은 방법이 있을거 같은데..
      for (const i in r.data) {
        const result: BrandVO = r.data[i];
        result.active = false;
        results.push(result);
      }
      this.brands = results;
    });
  }

  // 검색 필터 쿠폰 목록
  setCouponFilter(): void {
    if (!this.couponCd) {
      return;
    }
    axios.get(`/coupon/${this.couponCd}/brands`).then((r) => {
      const results = [] as Array<BrandVO>;
      // 각 행에 active = false 세팅. FIXME 더 나은 방법이 있을거 같은데..
      for (const i in r.data) {
        const result: BrandVO = r.data[i];
        result.active = false;
        results.push(result);
      }
      this.brands = results;
    });
    axios.get(`/coupon/${this.couponCd}/categories`).then((r) => {
      const results = [] as Array<CategoryVO>;
      // 각 행에 active = false 세팅. FIXME 더 나은 방법이 있을거 같은데..
      for (const i in r.data) {
        const result: CategoryVO = r.data[i];
        result.active = false;
        results.push(result);
      }
      this.categories = results;
    });
  }

  // 검색 필터 가격 목록
  setFilterPrices(): void {
    axios.get(`/product/prices`, { params: { cateCd: this.cateCd, brandCd: this.brandCd } }).then((r) => {
      this.priceIncrease.max = r.data.maxPrice;
      this.priceIncrease.min = r.data.minPrice;
      this.priceIncrease.value = [r.data.minPrice, r.data.maxPrice];
    });
  }

  // 빠른배송, 일반배송 필터
  setDeliveryTypes(): void {
    this.deliveryTypes.push({ productType: "02", productTypeNm: this.$t("product.t0001").toString(), active: false });
    this.deliveryTypes.push({ productType: "01", productTypeNm: this.$t("product.t0002").toString(), active: false });
  }

  onLoadMore() {
    // FIXME keep-alive 때문에 이벤트가 unbind 안돼서 일단 이렇게 했는데
    // this.isActive 같은 레퍼런스 함수가 있을거 같은데..
    if (!this.active) {
      return;
    }

    this.search(true);
  }
  activated(): void {
    this.active = true;
  }
  deactivated(): void {
    this.active = false;
  }

  btnZzim(product: ProductVO) {
    CommonUtils.zzimProduct(product).then();
  }

  // initialized events
  created(): void {
    this.setCategories();
    this.setBrands();
    this.setCouponFilter();
    this.setFilterPrices();
    this.setDeliveryTypes();
    this.search();
  }
}
